import { ApiResponse } from 'apisauce';
import * as R from 'ramda';
import storage from '../../../hooks/useStorage/useStorage';
import LangEnum from '../../../lang/LangEnum';
import { ErrorResponse, handlerApiError } from '../../../lib/basicRepository';
import snackbarStore from '../../../stores/Snackbar.store';
import { PaginatedResponse } from '../../../types/request.types';
import { createLinkToDownloadBlob } from '../../../utils/createLinkToDownload';
import { HTTP_STATUS } from '../../../utils/httpStatus.const';
import { OrderHistory } from '../../orders/order.types';
import { OrderProduct, UpdateItemFormat } from '../../orders/products/orderProducts.types';
import {
  onlineExportMultipleOrdersCsv,
  onlineOrdersRepository,
} from '../../orders/repositories/ordersRepository/onlineOrdersRepository';
import { GridItemSkuDTO } from '../../products/details/productDetails.types';
import { StackKit } from '../../products/products-details-v2/components/KitDetailsList/types/kitDetailsList.types';
import { OrderProductDeliveryWindows } from '../../products/stock/future-stock-v2/utils/dtos/order-product-delivery-windows.dto';
import {
  availabilityToAvailabilityEnum,
  toOfflineAvailability,
} from '../../products/utils/availability.utils';
import { User } from '../../user-admin/user.types';
import { OfflineOrderCsvBuilder } from '../builders/offlineOrderCsv/OfflineOrderCsv.builder';
import { offlineDeliveryWindowsRepository } from '../repositories/offlineDeliveryWindows.repository';
import { offlineFutureStocksRepository } from '../repositories/offlineFutureStocks.repository';
import { offlineKitsRepository } from '../repositories/offlineKits.repository';
import { offlineOrdersRepository } from '../repositories/offlineOrders.repository';
import { offlineProductColorsRepository } from '../repositories/offlineProductColors.repository';
import { offlineSkusRepository } from '../repositories/offlineSkus.repository';
import { offlineUsersRepository } from '../repositories/offlineUsers.repository';
import { useOfflineOrdersStore } from '../stores/offlineOrders/offlineOrders.store';
import {
  ChangeOrderProductAvailabilityParams,
  ChangeOrderProductColorParams,
  CreateGridParams,
  UpdateOfflineOrderGeneralData,
} from '../stores/offlineOrders/types/offlineOrders.actions.types';
import { OfflineDeliveryWindowTransformers } from '../transformers/OfflineDatabaseDeliveryWindow.transformers';
import { OfflineOrderTransformers } from '../transformers/OfflineOrder.transformers';
import { createApiSauceResponse } from '../utils/createApiSauceResponse.util';
import { createOrUpdateOrderListOfflineAdapter } from './offlineRequestAdaptersFunctions/createOrUpdateOrderList.offlineAdapter';
import {
  mountKitGridItemsToCreate,
  mountKitGridItemsToUpdate,
  mountSkuKitsMap,
  sortOrderProducts,
  validateKitHasConflictsOnOrderProductsAndReturnError,
} from './utils/onlineOrderRepository.utils';
import { notImplementedInOfflineError } from './utils/repository.offlineAdapters.utils';

const { handleMessage } = snackbarStore;

export const offlineExportMultipleOrdersCsv: typeof onlineExportMultipleOrdersCsv = async ({
  filename,
  searchParams,
  translate,
}) => {
  const offlineOrders = searchParams?.ids?.length
    ? await offlineOrdersRepository.findByIds(searchParams.ids)
    : await offlineOrdersRepository.getAll();

  try {
    const offlineOrderCsvBuilder = new OfflineOrderCsvBuilder(offlineOrders);

    const csvData = offlineOrderCsvBuilder.build();

    createLinkToDownloadBlob({
      filename,
      archiveType: 'csv',
      data: csvData,
    });

    return createApiSauceResponse({
      ok: true,
      data: csvData,
    });
  } catch (error) {
    console.error('exportCsv', {
      error,
      offlineOrders,
    });

    handleMessage({
      type: 'error',
      message: translate(LangEnum.SNACKBAR_ERROR_DOWNLOADING_ORDER_CSV, {
        isOffline: true,
      }),
    });

    return createApiSauceResponse({
      ok: false,
    });
  }
};

type OnlineOrderRepositoryOfflineAdapters = ReturnType<typeof onlineOrdersRepository> & {
  getOrderHistories: () => Promise<ApiResponse<PaginatedResponse<OrderHistory>>>;
};

export const onlineOrderRepositoryOfflineAdapters = (
  id?: string,
): OnlineOrderRepositoryOfflineAdapters => {
  const orderId = id ?? '';

  const createOrUpdateOrderList = async (items: UpdateItemFormat[], kitId?: string) => {
    try {
      const groupedOrderItemsAsRecord = R.groupBy(
        (item) => `${item.availability}|${item.deliveryWindowId}|${item.productColorId}`,
        items,
      );

      const groupedOrderItems = Object.values(groupedOrderItemsAsRecord);

      const returnedDtos: Awaited<
        ReturnType<typeof createOrUpdateOrderListOfflineAdapter>
      >['skus'][] = [];

      for (let i = 0; i < groupedOrderItems.length; i++) {
        const dtoAfterUpdateGrid = await createOrUpdateOrderListOfflineAdapter({
          orderId,
          kitId,
          items: groupedOrderItems[i] ?? [],
        });

        returnedDtos.push(dtoAfterUpdateGrid.skus);
      }

      const onlySkusToReturn = returnedDtos.flat();

      return handlerApiError(
        createApiSauceResponse<GridItemSkuDTO[], ErrorResponse>({
          ok: true,
          data: onlySkusToReturn,
        }),
      );
    } catch (error) {
      console.error('createOrderProduct', { error });
      return handlerApiError(
        createApiSauceResponse({
          ok: false,
          data: {
            message: 'erro ao adicionar produto ao pedido',
            code: LangEnum.OFFLINE_ERROR_ERROR_ADDING_PRODUCT_IN_ORDER,
          } as any,
        }),
      );
    }
  };

  return {
    createOrUpdateOrderList,
    create: async (data) => {
      try {
        const order = await useOfflineOrdersStore.getState().create(data?.customerId);
        const onlineOrder = OfflineOrderTransformers.toOnlineOrder(order);
        return createApiSauceResponse<any>({
          ok: true,
          data: onlineOrder,
        });
      } catch (e) {
        const error = e as Error;
        console.error('create', { error });
        return createApiSauceResponse<any>({
          ok: false,
          data: {
            message: 'erro ao criar pedido',
            code: LangEnum.API_EXCEPTION_UNABLE_TO_CREATE_ORDER,
          },
        });
      }
    },
    getOne: async () => {
      const order = await offlineOrdersRepository.getById(orderId);
      if (!order) {
        return createApiSauceResponse({
          ok: false,
          data: {
            message: 'pedido não encontrado',
            code: LangEnum.API_EXCEPTION_ORDER_NOT_FOUND,
          },
        });
      }
      const onlineOrder = OfflineOrderTransformers.toOnlineOrder(order);
      return createApiSauceResponse<any>({
        ok: true,
        data: onlineOrder,
      });
    },
    update: async (data: UpdateOfflineOrderGeneralData) => {
      try {
        const order = await useOfflineOrdersStore.getState().updateGeneralData(orderId, data);
        const onlineOrder = OfflineOrderTransformers.toOnlineOrder(order);
        return createApiSauceResponse<any>({
          ok: true,
          data: onlineOrder,
        });
      } catch (error) {
        console.error('update', { error });

        return createApiSauceResponse<any>({
          ok: false,
          data: {
            message: 'pedido não encontrado',
            code: LangEnum.API_EXCEPTION_ORDER_NOT_FOUND,
          },
        });
      }
    },

    updateOrderCustomer: async (customerId: string | null) => {
      try {
        const order = await useOfflineOrdersStore
          .getState()
          .updateOrderCustomer(orderId, customerId);
        const onlineOrder = OfflineOrderTransformers.toOnlineOrder(order);
        return createApiSauceResponse<any>({
          ok: true,
          data: onlineOrder,
        });
      } catch (error) {
        console.error('updateOrderCustomer', { error });
        const errorData = (error as { data?: Partial<ErrorResponse> }).data;
        const errorCode = errorData?.code;
        if (errorCode && Object.values(LangEnum).includes(errorCode)) {
          return createApiSauceResponse<any, ErrorResponse>({
            ok: false,
            data: {
              code: errorCode,
              message: errorData?.message || '',
              status: errorData?.status || HTTP_STATUS.BAD_REQUEST,
            },
          });
        }
        return createApiSauceResponse<any>({
          ok: false,
          data: {
            message: 'pedido não encontrado',
            code: LangEnum.API_EXCEPTION_ORDER_NOT_FOUND,
          },
        });
      }
    },

    getOrderProductDeliveryWindows: async (productId: string, productColorId: string) => {
      const offlineProductColor = await offlineProductColorsRepository.getById(productColorId);

      if (!offlineProductColor) {
        return createApiSauceResponse<OrderProductDeliveryWindows, ErrorResponse>({
          ok: false,
          data: {
            message: 'produto cor não encontrado',
            code: LangEnum.API_EXCEPTION_PRODUCT_COLOR_NOT_FOUND,
          },
        });
      }

      const [offlineSkus, offlineFutureStocks] = await Promise.all([
        offlineSkusRepository.findByProductColorId(offlineProductColor.id),
        offlineFutureStocksRepository.findByProductAndColorId({ productId, productColorId }),
      ]);

      const uniqOfflineFutureStocks = R.uniqBy(R.prop('deliveryWindowId'), offlineFutureStocks);

      const futureStockDeliveryWindowIds = uniqOfflineFutureStocks.map(
        (stock) => stock.deliveryWindowId,
      );
      const preOrderDeliveryWindowIds = offlineSkus
        .flatMap((sku) => sku.preOrderDeliveryWindowIds)
        .filter(Boolean) as string[];

      const deliveryWindowIdsToFind = [
        ...preOrderDeliveryWindowIds,
        ...futureStockDeliveryWindowIds,
      ];

      const uniqDeliveryWindowIds = R.uniq(deliveryWindowIdsToFind);

      const deliveryWindows =
        await offlineDeliveryWindowsRepository.findByIds(uniqDeliveryWindowIds);

      if (!deliveryWindows.length) {
        return createApiSauceResponse<OrderProductDeliveryWindows, ErrorResponse>({
          ok: true,
          data: {
            infinitePreOrderDeliveryWindows: [],
            materialPreOrderDeliveryWindows: [],
            futureStockDeliveryWindows: [],
          },
        });
      }

      const futureStockDeliveryWindows = deliveryWindows
        .filter((window) => futureStockDeliveryWindowIds.includes(window.id))
        .map(OfflineDeliveryWindowTransformers.toDeliveryWindow);

      const preOrderDeliveryWindows = deliveryWindows
        .filter((window) => preOrderDeliveryWindowIds.includes(window.id))
        .map(OfflineDeliveryWindowTransformers.toDeliveryWindow);

      return handlerApiError(
        createApiSauceResponse<OrderProductDeliveryWindows, ErrorResponse>({
          ok: true,
          data: {
            infinitePreOrderDeliveryWindows: preOrderDeliveryWindows,
            materialPreOrderDeliveryWindows: [],
            futureStockDeliveryWindows,
          },
        }),
      );
    },
    getOrderProductsV2: async (params) => {
      const order = await offlineOrdersRepository.getById(orderId);

      if (!order) {
        return createApiSauceResponse({
          ok: false,
          data: {
            message: 'pedido não encontrado',
            code: LangEnum.API_EXCEPTION_ORDER_NOT_FOUND,
          } as any,
        });
      }

      const orderProducts = OfflineOrderTransformers.toOrderProducts(order);

      const sortedOrderProducts = sortOrderProducts(orderProducts, params?.sortBy);

      return createApiSauceResponse({
        ok: true,
        data: sortedOrderProducts,
      });
    },
    createOrderProduct: async (items: Array<UpdateItemFormat>) => {
      try {
        const allItemsLackQuantity = items.every((item) => !item.quantity);

        const allItemsValid = items.every((item) => item.skuId && item.availability);

        if (allItemsLackQuantity || !allItemsValid) {
          throw new Error('missing required fields');
        }

        // @ts-ignore
        const itemsAsCreateGridParams = items.map<CreateGridParams>((item) => ({
          skuId: item.skuId!,
          quantity: item.quantity!,
          productColorId: item.productColorId,
          availability: toOfflineAvailability(availabilityToAvailabilityEnum(item.availability)),
          deliveryWindowId: item.deliveryWindowId ? item.deliveryWindowId : undefined,
        }));

        const TECEO_IN_NUMBERS = 2053515;

        const shouldAddAllProductsToTest = items.some((item) => item.quantity === TECEO_IN_NUMBERS);
        if (shouldAddAllProductsToTest) {
          await useOfflineOrdersStore.getState().addAllPossibleProductsInOrder(orderId);
        }

        const order = await useOfflineOrdersStore
          .getState()
          .addProductBasedOnGrid({ orderId, gridItems: itemsAsCreateGridParams });

        const lastAddedOrderProduct = order.getLastAddedProduct()!;

        const createdOrderProduct =
          OfflineOrderTransformers.offlineOrderProductToOrderProduct(lastAddedOrderProduct);

        return handlerApiError(
          createApiSauceResponse<OrderProduct[], ErrorResponse>({
            ok: true,
            data: [createdOrderProduct],
          }),
        );
      } catch (error) {
        console.error('createOrderProduct', { error });
        return handlerApiError(
          createApiSauceResponse({
            ok: false,
            data: {
              message: 'erro ao adicionar produto ao pedido',
              code: LangEnum.OFFLINE_ERROR_ERROR_ADDING_PRODUCT_IN_ORDER,
            } as any,
          }),
        );
      }
    },
    changeOrderProductDeliveryWindow: async (items) => {
      try {
        const itemWithId = items.find((item) => item.id);
        if (!itemWithId) {
          throw new Error('order item id is required for change availability');
        }

        const { availability, deliveryWindowId, id } = itemWithId;

        if (!availability || !id) {
          console.error('changeOrderProductDeliveryWindow', {
            availability,
            id,
          });
          throw new Error('missing required fields');
        }

        const paramsToChangeAvailability: ChangeOrderProductAvailabilityParams = {
          anyGridItemId: id,
          deliveryWindowId: deliveryWindowId ? deliveryWindowId : undefined,
          newAvailability: availabilityToAvailabilityEnum(availability),
        };

        const order = await useOfflineOrdersStore
          .getState()
          .changeOrderProductAvailability(orderId, paramsToChangeAvailability);

        const offlineLastOrderProduct = order.getLastAddedProduct()!;

        const orderProduct =
          OfflineOrderTransformers.offlineOrderProductToOrderProduct(offlineLastOrderProduct);

        return handlerApiError(
          createApiSauceResponse<OrderProduct, ErrorResponse>({
            ok: true,
            data: orderProduct,
          }),
        );
      } catch (error) {
        console.error('changeOrderProductDeliveryWindow', { error });
        return handlerApiError(
          createApiSauceResponse({
            ok: false,
            data: {
              message: 'erro ao alterar disponibilidade',
              code: LangEnum.OFFLINE_ERROR_ALTERING_AVAILABILITY,
            } as any,
          }),
        );
      }
    },
    changeOrderProductColor: async ({ orderItemIds, newProductColorId }) => {
      try {
        const itemId = orderItemIds[0];

        const paramsToChangeColor: ChangeOrderProductColorParams = {
          newProductColorId,
          anyGridItemId: itemId,
        };

        const order = await useOfflineOrdersStore
          .getState()
          .changeOrderProductColor(orderId, paramsToChangeColor);

        const offlineLastOrderProduct = order.getLastAddedProduct()!;

        const orderProduct =
          OfflineOrderTransformers.offlineOrderProductToOrderProduct(offlineLastOrderProduct);

        return handlerApiError(
          createApiSauceResponse<OrderProduct, ErrorResponse>({
            ok: true,
            data: orderProduct,
          }),
        );
      } catch (error) {
        console.error('changeOrderProductColor', { error });
        return handlerApiError(
          createApiSauceResponse({
            ok: false,
            data: {
              message: 'erro ao alterar disponibilidade',
              code: LangEnum.OFFLINE_ERROR_ALTERING_AVAILABILITY,
            } as any,
          }),
        );
      }
    },
    deleteOrderProduct: async ({ productColorId, availabilities, deliveryWindowId }) => {
      try {
        if (!productColorId || !availabilities) {
          console.error('deleteOrderProduct', {
            productColorId,
            availabilities,
          });
          throw new Error('missing required fields');
        }

        const availability = availabilityToAvailabilityEnum(availabilities[0]);

        await useOfflineOrdersStore.getState().removeOrderProduct(orderId, {
          productColorId,
          deliveryWindowId,
          availability: availabilityToAvailabilityEnum(availability),
        });

        return handlerApiError(
          createApiSauceResponse({
            ok: true,
          }),
        );
      } catch (error) {
        console.error('changeOrderProductColor', { error });
        return handlerApiError(
          createApiSauceResponse({
            ok: false,
            data: {
              message: 'erro ao alterar disponibilidade',
              code: LangEnum.OFFLINE_ERROR_ALTERING_AVAILABILITY,
            },
          }),
        );
      }
    },
    getOrderHistories: async () => {
      const order = await offlineOrdersRepository.getById(orderId);

      if (!order) {
        return createApiSauceResponse({
          ok: false,
          data: {
            message: 'pedido não encontrado',
          } as any,
        });
      }

      const teamId = storage.getTeamId();
      const userTeam = offlineUsersRepository.getUserTeam(teamId);

      const user: User | undefined = userTeam
        ? {
            cpf: '',
            teams: [],
            phone: '',
            surname: '',
            id: userTeam.id,
            profileUrl: '',
            status: 'ACTIVE',
            name: userTeam.name,
            email: userTeam.email,
          }
        : undefined;

      const orderHistories = OfflineOrderTransformers.toOrderHistories(order, user);

      return createApiSauceResponse({
        ok: true,
        data: {
          data: orderHistories,
          count: orderHistories.length,
        },
      });
    },
    createOrUpdateOrderListWithKit: async (kit: StackKit) => {
      const offlineKit = await offlineKitsRepository.getById(kit.kitId);
      if (!offlineKit || !offlineKit.skuKits.length) {
        return handlerApiError(
          createApiSauceResponse({
            ok: false,
            data: {
              message: 'Kit não encontrado',
              code: LangEnum.API_EXCEPTION_UPDATE_OR_CREATE_ORDER_ITEMS_KIT_NOT_FOUND,
              status: HTTP_STATUS.NOT_FOUND,
            },
          }),
        );
      }

      const offlineOrder = await offlineOrdersRepository.getById(orderId);
      if (offlineOrder?.products && offlineOrder.products.length > 0) {
        const orderProductCombinationsMap = offlineOrder.getOrderProductCombinationsMap();

        const error = validateKitHasConflictsOnOrderProductsAndReturnError({
          orderProductCombinationsMap,
          kitId: offlineKit.id,
          skuKits: offlineKit.skuKits,
        });

        if (error) {
          return error;
        }
      }

      const kitOrderProducts = offlineOrder?.products?.filter(
        (product) => product.kit?.id === offlineKit.id,
      );

      const gridItems =
        kitOrderProducts && kitOrderProducts.length
          ? mountKitGridItemsToUpdate({
              kitOrderProducts,
              skuKitsMap: mountSkuKitsMap(offlineKit.skuKits),
              kitQuantity: kit.quantity,
            })
          : await mountKitGridItemsToCreate(offlineKit.skuKits, kit.quantity);

      const response = await createOrUpdateOrderList(gridItems, kit.kitId);

      return response;
    },
    archiveOrder: () => notImplementedInOfflineError('archiveOrder'),
    changeOrderStatus: () => notImplementedInOfflineError('changeOrderStatus'),
    forceOrderSync: () => notImplementedInOfflineError('forceOrderSync'),
    forceReverseOrderSync: () => notImplementedInOfflineError('forceReverseOrderSync'),
    getBrandSettings: () => notImplementedInOfflineError('getBrandSettings'),
    getOrderConditionsErrors: () => notImplementedInOfflineError('getOrderConditionsErrors'),
    getOrderErrors: () => notImplementedInOfflineError('getOrderErrors'),
    getOrderGeneralDataErrors: () => notImplementedInOfflineError('getOrderGeneralDataErrors'),
    getPaymentHistory: () => notImplementedInOfflineError('getPaymentHistory'),
    getValidateWarnings: () => notImplementedInOfflineError('getValidateWarnings'),
    shareOrder: () => notImplementedInOfflineError('shareOrder'),
    unarchiveOrder: () => notImplementedInOfflineError('unarchiveOrder'),
    validateOrUpdateOrderConditions: () =>
      notImplementedInOfflineError('validateOrUpdateOrderConditions'),
    validateOrUpdateOrderItemPrices: () =>
      notImplementedInOfflineError('validateOrUpdateOrderItemPrices'),
    remove: () => notImplementedInOfflineError('remove'),
    authorizeOrderPayment: () => notImplementedInOfflineError('authorizeOrderPayment'),
    getOrderPaymentData: () => notImplementedInOfflineError('getOrderPaymentData'),
    createOrderPaymentLink: () => notImplementedInOfflineError('createOrderPaymentLink'),
    sendPaymentLinkEmails: () => notImplementedInOfflineError('sendPaymentLinkEmails'),
    cancelOrderPaymentLink: () => notImplementedInOfflineError('cancelOrderPaymentLink'),
    refundOrderPaymentLink: () => notImplementedInOfflineError('refundOrderPaymentLink'),
  };
};
